import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { envConfig } from '../../../config';
import { getAccessToken } from '../../../auth';
import { EditorService } from '../../../services/editor';
import Button from '../../../lib/button/Button';
import Loader from '../../../lib/loader/Loader';
import { getFileIcon } from './CloudFileSystemBucketExplorer';
import ElyxMonacoEditor from '../../editor/ElyxMonacoEditor';
import { popupNotification } from '../../../popup-notification';

interface IFileViewerProps {
	project: EditorService;
	resourceId: string;
}

const isElyxUri = (u: string): boolean => {
	if (!u) return false;
	const isHttp = u.startsWith('http://') || u.startsWith('https://');
	if (!isHttp) return false;
	try {
		const url = new URL(u, window.location.origin);
		return (
			url.hostname?.endsWith('elyx.tech') ||
			/^\/v\d+\/cloud-files-service\//.test(url.pathname)
		);
	} catch (_) {
		return false;
	}
};

const fetchWithAuthIfNeeded = async (uri: string) => {
	let headers: Record<string, string> | undefined = undefined;

	if (isElyxUri(uri)) {
		try {
			const token = await getAccessToken();
			if (token) {
				headers = {
					Authorization: `Bearer ${token}`
				};
			}
		} catch (_) {}
	}

	const response = await fetch(uri, { headers });
	if (!response.ok) {
		throw new Error(
			`Failed to fetch file from uri (${response.status}): ${uri}`
		);
	}
	return response;
};

const FileViewer: React.FC<IFileViewerProps> = ({ project, resourceId }) => {
	const navigate = useNavigate();
	const location = useLocation();

	// Extract the file key from the wildcard route part
	// Example path: /resources/cloud-file-system-bucket/res123/view/dist/index.html
	const pathParts = location.pathname.split('/view/');
	const fileKey =
		pathParts.length > 1 ? decodeURIComponent(pathParts[1]) : '';

	const [fileUrl, setFileUrl] = useState<string>('');
	const [fileContent, setFileContent] = useState<string>('');
	const [isLoading, setIsLoading] = useState(true);

	const fileTypeInfo = getFileIcon(fileKey);

	useEffect(() => {
		const loadFile = async () => {
			setIsLoading(true);
			try {
				const token = await getAccessToken();
				const presignedRes = await fetch(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/presigned-url?bucket=${resourceId}&key=${encodeURIComponent(fileKey)}&action=read`,
					{
						headers: {
							Authorization: `Bearer ${token}`
						}
					}
				);
				const presignedData = await presignedRes.json();
				let targetUrl = presignedData?.url;

				if (!targetUrl) {
					targetUrl = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(fileKey)}${token ? `&token=${token}` : ''}`;
				} else if (
					isElyxUri(targetUrl) &&
					token &&
					!targetUrl.includes('token=')
				) {
					const separator = targetUrl.includes('?') ? '&' : '?';
					targetUrl = `${targetUrl}${separator}token=${encodeURIComponent(token)}`;
				}

				setFileUrl(targetUrl);

				if (
					fileTypeInfo.type === 'code' ||
					fileTypeInfo.type === 'text'
				) {
					const contentResponse =
						await fetchWithAuthIfNeeded(targetUrl);
					const text = await contentResponse.text();
					setFileContent(text);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setIsLoading(false);
			}
		};
		if (fileKey) loadFile();
	}, [fileKey, resourceId, fileTypeInfo.type]);

	const handleBack = () => {
		// Go back to the explorer, trying to preserve the path query param
		// The path would be the directory of the file
		const dirPath = fileKey.substring(0, fileKey.lastIndexOf('/') + 1);
		navigate(`../?path=${encodeURIComponent(dirPath)}`);
	};

	const copyUrl = () => {
		if (fileUrl) {
			navigator.clipboard.writeText(fileUrl);
			popupNotification({
				text: 'URL copied to clipboard!',
				color: 'success'
			});
		}
	};

	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownload = async () => {
		if (!fileKey || isDownloading) return;
		setIsDownloading(true);
		const fileName = fileKey.split('/').pop() || 'file';
		popupNotification({
			text: `Downloading "${fileName}"...`,
			color: 'primary'
		});
		try {
			const rawUrl = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(fileKey)}`;
			const resp = await fetch(rawUrl, {
				headers: {
					Authorization: `Bearer ${await getAccessToken()}`
				}
			});
			const blob = await resp.blob();
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			popupNotification({
				text: `Downloaded "${fileName}"`,
				color: 'success'
			});
		} catch (e) {
			popupNotification({
				text: 'Failed to download file',
				color: 'error'
			});
		} finally {
			setIsDownloading(false);
		}
	};

	if (!fileKey) return null;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				backgroundColor: '#f8fafc'
			}}
		>
			{/* Header */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '12px 24px',
					borderBottom: '1px solid #e2e8f0',
					backgroundColor: '#fff'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						onClick={handleBack}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							color: '#64748b'
						}}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px'
						}}
					>
						<i
							className={fileTypeInfo.className}
							style={{ color: fileTypeInfo.color }}
						></i>
						<span style={{ fontWeight: 500, color: '#1e293b' }}>
							{fileKey}
						</span>
					</div>
				</div>
				<div style={{ display: 'flex', gap: '8px' }}>
					<Button
						variant="outlined"
						size="small"
						iconStart="fa-solid fa-download"
						loading={isDownloading}
						disabled={isDownloading}
						onClick={handleDownload}
					>
						Download
					</Button>
					<Button
						variant="outlined"
						size="small"
						iconStart="fa-regular fa-copy"
						onClick={copyUrl}
						disabled={!fileUrl}
					>
						Copy URL
					</Button>
					{fileUrl && (
						<Button
							variant="outlined"
							size="small"
							iconStart="fa-solid fa-external-link-alt"
							onClick={() => window.open(fileUrl, '_blank')}
						>
							Open in new tab
						</Button>
					)}
				</div>
			</div>

			{/* Content */}
			<div
				style={{
					flex: 1,
					overflow: 'auto',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center'
				}}
			>
				{isLoading ? (
					<Loader />
				) : !fileUrl ? (
					<div style={{ color: '#64748b' }}>
						File not found or access denied
					</div>
				) : (
					<div
						style={{
							width: '100%',
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
					>
						{fileTypeInfo.type === 'image' && (
							<img
								src={fileUrl}
								alt={fileKey}
								style={{
									maxWidth: '100%',
									maxHeight: '100%',
									objectFit: 'contain'
								}}
							/>
						)}
						{fileTypeInfo.type === 'video' && (
							<video
								src={fileUrl}
								controls
								style={{ maxWidth: '100%', maxHeight: '100%' }}
							/>
						)}
						{fileTypeInfo.type === 'audio' && (
							<audio src={fileUrl} controls />
						)}
						{fileTypeInfo.type === 'document' &&
							fileKey.endsWith('.pdf') && (
								<iframe
									src={fileUrl}
									style={{
										width: '100%',
										height: '100%',
										border: 'none'
									}}
									title={fileKey}
								/>
							)}
						{(fileTypeInfo.type === 'code' ||
							fileTypeInfo.type === 'text') && (
							<div
								style={{
									width: '100%',
									height: '100%',
									textAlign: 'left'
								}}
							>
								<ElyxMonacoEditor
									value={fileContent}
									language={(() => {
										const ext = fileKey
											.split('.')
											.pop()
											?.toLowerCase();
										const map: Record<string, string> = {
											ts: 'typescript',
											tsx: 'typescript',
											js: 'javascript',
											jsx: 'javascript',
											json: 'json',
											html: 'html',
											css: 'css',
											md: 'markdown',
											yml: 'yaml',
											yaml: 'yaml',
											xml: 'xml',
											sql: 'sql',
											sh: 'shell'
										};
										return map[ext || ''] || 'text';
									})()}
									options={{
										readOnly: true,
										minimap: { enabled: false }
									}}
								/>
							</div>
						)}
						{fileTypeInfo.type === 'unknown' ||
						fileTypeInfo.type === 'executable' ||
						fileTypeInfo.type === 'archive' ||
						(fileTypeInfo.type === 'document' &&
							!fileKey.endsWith('.pdf')) ? (
							<div
								style={{
									textAlign: 'center',
									color: '#64748b'
								}}
							>
								<i
									className={fileTypeInfo.className}
									style={{
										fontSize: '48px',
										color: '#cbd5e1',
										marginBottom: '16px'
									}}
								></i>
								<p>No preview available for this file type.</p>
								<Button
									as="a"
									href={fileUrl}
									target="_blank"
									variant="outlined"
									style={{ marginTop: '16px' }}
								>
									Download File
								</Button>
							</div>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export default FileViewer;
